<template>
  <div class="channel-messages" @scroll="onScroll">
    <HAbortHandler ref="abortHandler" />
    <CMessagePlaceholder v-if="displayInitialLoading" />
    <HCardPlaceholder
      v-else-if="!endPagination"
      ref="placeholder"
      v-observe-visibility="{
        callback: loadNextMessages,
        threshold: 0.3
      }"
      :count="1"
    />
    <div v-else-if="!currentMessages.length" class="channel-messages__empty">
      Nenhuma mensagem
    </div>

    <CMessage
      v-for="(message, index) in currentMessages"
      :key="message.id"
      ref="message"
      :only-view="onlyView"
      :message="message"
      :is-private="isPrivate"
      :next-message="currentMessages[index + 1]"
      :previous-message="currentMessages[index - 1]"
      :channel-id="currentChannel.id"
      :should-highlight="message.id && highlight === message.id"
      :reaction-icons="reactionIcons"
      :data-paginate-scroll="
        (lastMessagePaginateId && message.id === lastMessagePaginateId) || null
      "
      @on-play="handleMedia"
      @on-internal-redirect="$emit('onInternalRedirect', $event)"
    />

    <CChannelTyping v-if="!onlyView" :is-private="isPrivate" />

    <TransitionExpand>
      <div
        v-if="showNewMessagesButton"
        class="channel-messages__alert"
        @click="focusNewMessages"
      >
        <WIcon icon="icArrowDown" size="medium" />
        Ver novas mensagens
      </div>
    </TransitionExpand>
  </div>
</template>

<script>
import { WIcon } from '@alice-health/wonderland-vue'
import debounce from 'lodash/debounce'
import { nextTick } from 'vue'
import { mapGetters, mapState } from 'vuex'

import {
  HAbortHandler,
  HCardPlaceholder,
  TransitionExpand
} from '@/components/shared/atoms'
import { MessageFocus } from '@/models/channels'

import { CMessagePlaceholder } from '../atoms'
import { CChannelTyping, CMessage } from '../molecules'

export default {
  components: {
    WIcon,
    CMessage,
    CChannelTyping,
    TransitionExpand,
    HCardPlaceholder,
    CMessagePlaceholder,
    HAbortHandler
  },
  props: {
    isPrivate: {
      type: Boolean,
      default: false
    },
    currentChannel: {
      type: Object,
      default: () => ({})
    },
    onlyView: {
      type: Boolean,
      default: false
    }
  },
  emits: ['onInternalRedirect'],
  data() {
    return {
      showNewMessagesButton: false,
      focusType: MessageFocus.LATEST_SYNCED,
      reactionIcons: [],
      lastMessagePaginateId: null,
      abortHandlerSignal: null
    }
  },
  computed: {
    ...mapState('pillars/channel/message', ['highlight', 'endPagination']),
    ...mapGetters('pillars/channel/message', ['currentMessages']),
    displayInitialLoading() {
      return !this.currentMessages.length && !this.endPagination
    }
  },
  watch: {
    isPrivate() {
      this.loadMessages()
    },
    'currentMessages.length'() {
      this.handleNewMessages()
    },
    highlight() {
      const message = this.$refs.message?.find(
        ref => ref.message.id === this.highlight
      )?.$el
      if (!message) return
      this.$el.scrollTo({
        top: message.offsetTop - 33,
        behavior: 'smooth'
      })
    }
  },
  mounted() {
    this.loadMessages()
    this.getReactionIcons()
    this.abortHandlerSignal = this.$refs.abortHandler?.getSignal()
  },
  methods: {
    loadNextMessages(isVisible) {
      if (!isVisible) return
      this.focusType = MessageFocus.PAGINATION
      this.lastMessagePaginateId = this.getLastMessageId()
      this.$store.dispatch('pillars/channel/message/fetchMessages', {
        paginating: true,
        signal: this.abortHandlerSignal,
        channel: this.currentChannel
      })
    },
    getLastMessageId() {
      const lastMessage = this.currentMessages.find(item => item.id)
      return lastMessage?.id
    },
    async loadMessages() {
      const paginating = true
      await this.$store.dispatch('pillars/channel/message/fetchMessages', {
        paginating,
        signal: this.abortHandlerSignal,
        channel: this.currentChannel
      })
      this.focusType = MessageFocus.LATEST_SYNCED
    },
    async getReactionIcons() {
      this.reactionIcons = await this.$store.dispatch(
        'pillars/channel/message/getReactionIcons'
      )
    },
    handleMedia() {
      this.$refs.message.forEach(message => {
        const audio = message.$refs.audio
        if (audio?.isPlaying) audio.pause()
        const video = message.$refs.video
        if (video?.isPlaying) video.pause()
      })
    },
    async handleFocus() {
      await nextTick()

      switch (this.focusType) {
        case MessageFocus.NEWEST:
          this.focusNewMessages()
          break

        case MessageFocus.LATEST_SYNCED:
          this.focusLatestSyncedMessage()
          break
        case MessageFocus.PAGINATION:
          this.focusPagination()
          break
        default:
      }
    },
    focusNewMessages() {
      const messagesBox = this.$el
      messagesBox.scrollTop = messagesBox.scrollHeight
      this.focusType = null
      this.showNewMessagesButton = false
    },
    focusLatestSyncedMessage() {
      const messageBar = this.$el.querySelector('[data-new-message-bar]')
      if (messageBar) {
        messageBar.scrollIntoView({ block: 'center' })
        this.focusType = null
        this.showNewMessagesButton = false
      } else {
        this.focusNewMessages()
      }
    },
    focusPagination() {
      const lastPaginateMessage = this.$el.querySelector(
        '[data-paginate-scroll]'
      )

      if (lastPaginateMessage) {
        this.$el.scrollTo({
          top: lastPaginateMessage.offsetTop - 10
        })
      }
      this.focusType = null
    },
    onScroll: debounce(function ({ target }) {
      if (!target || this.focusType === MessageFocus.PAGINATION) {
        return
      }
      const scroll = target.scrollTop + target.clientHeight

      if (scroll < target.scrollHeight) {
        this.focusType = MessageFocus.NONE
      } else {
        this.focusType = null
        this.showNewMessagesButton = false
      }
    }, 300),
    async handleNewMessages() {
      if (this.focusType !== MessageFocus.PAGINATION) {
        await nextTick()
        const messageBar = this.$el.querySelector('[data-new-message-bar]')
        const latestMessage =
          this.currentMessages[this.currentMessages.length - 1]

        if (!this.focusType || latestMessage?.isFromCurrentUser) {
          this.focusType = MessageFocus.NEWEST
        } else if (messageBar) {
          this.showNewMessagesButton = true
        }
      }

      this.handleFocus()
    }
  }
}
</script>

<style lang="scss">
@import '@/style/_non-rendering';
.channel-messages {
  width: 100%;
  padding: var(--gl-spacing-02) 0;
  position: relative;

  &__alert {
    @extend %caption;
    cursor: pointer;
    display: flex;
    align-items: center;
    width: max-content;
    bottom: 0;
    gap: var(--gl-spacing-02);
    padding: var(--gl-spacing-01) var(--gl-spacing-03);
    background: var(--channel-new-message, var(--gl-color-shades-gray-90));
    color: var(--gl-color-shades-gray-00);
    border-radius: var(--gl-border-radius-lg);
    margin: auto;
    position: sticky;
    z-index: 3;
  }

  &__empty {
    text-align: center;
    padding-top: var(--gl-spacing-06);
    color: var(--message-visualization-color);
  }
}
</style>
